import React from 'react'
import styled from 'styled-components'

import DocumentTitle from './DocumentTitle'
import InfoPage from './InfoPage'
import Link from './Link'
import Searchbox from './Searchbox'

import ipnLogo from './Images/LOGO_IPN_GREEN.png'
import bannerImage from './Images/BANNER.png'

const GREEN = '#00552e'
const DARK_GREEN = '#003d21'
const TEXT = '#111820'
const MUTED = '#5b6570'
const BORDER = '#d8dde1'
const LIGHT_GREEN = '#f3f8f5'

/* =========================================================
   PAGE
   ========================================================= */

const HomePage = styled(InfoPage)`
  width: 100%;
  max-width: none;

  margin: 0;
  padding: 0;

  background: #fff;
  color: ${TEXT};

  display: flex;
  flex-direction: column;

  font-family: Arial, Helvetica, sans-serif;
`

/* =========================================================
   SHARED WIDTH
   ========================================================= */

const Content = styled.div`
  width: calc(100% - clamp(36px, 8vw, 180px));
  max-width: 1280px;
  margin: 0 auto;

  box-sizing: border-box;

  @media (max-width: 700px) {
    width: calc(100% - 32px);
  }
`

/* =========================================================
   HERO
   ========================================================= */

const HeroSection = styled.section`
  position: relative;

  width: 100%;
  height: clamp(360px, 38vw, 500px);

  box-sizing: border-box;
  overflow: hidden;

  display: flex;
  align-items: center;

  border-bottom: 1px solid #e7ebe8;

  /*
   * Keep the original green/white gradient behind everything.
   * The banner image sits on top of this background.
   */
  background:
    radial-gradient(
      circle at 78% 48%,
      rgba(0, 85, 46, 0.08) 0%,
      rgba(0, 85, 46, 0.03) 24%,
      transparent 52%
    ),
    linear-gradient(
      90deg,
      #ffffff 0%,
      #ffffff 43%,
      #f3f8f5 100%
    );

  /*
   * On smaller screens, give the hero enough height
   * for the title and description while still keeping
   * the banner behind them.
   */
  @media (max-width: 700px) {
    height: clamp(340px, 70vw, 440px);
  }
`

const HeroContent = styled(Content)`
  position: relative;
  z-index: 2;

  width: calc(100% - clamp(36px, 8vw, 180px));

  @media (max-width: 700px) {
    width: calc(100% - 32px);
  }
`

const HeroCopy = styled.div`
  position: relative;
  z-index: 3;

  width: min(560px, 100%);

  /*
   * Once the banner starts moving behind the title,
   * add a subtle white fade behind the text so it
   * remains readable without completely hiding the image.
   */
  @media (max-width: 1000px) {
    box-sizing: border-box;

    padding:
      clamp(16px, 2vw, 24px)
      clamp(16px, 2vw, 24px);

    margin-left: clamp(-24px, -2vw, -16px);

    border-radius: 12px;

    background: linear-gradient(
      90deg,
      rgba(255, 255, 255, 0.97) 0%,
      rgba(255, 255, 255, 0.91) 45%,
      rgba(255, 255, 255, 0.65) 72%,
      rgba(255, 255, 255, 0) 100%
    );
  }

  @media (max-width: 600px) {
    width: 100%;

    background: linear-gradient(
      90deg,
      rgba(255, 255, 255, 0.98) 0%,
      rgba(255, 255, 255, 0.90) 55%,
      rgba(255, 255, 255, 0.35) 82%,
      rgba(255, 255, 255, 0) 100%
    );
  }
`

const BannerImage = styled.img`
  /*
   * The banner is attached directly to the HERO,
   * not the Content container.
   *
   * top: 0 + bottom: 0 + height: 100%
   * means the image always fills the full hero height.
   */
  position: absolute;

  top: 0;
  right: 0;
  bottom: 0;

  height: 100%;
  width: auto;

  max-width: none;

  object-fit: contain;
  object-position: right center;

  opacity: 0.95;

  pointer-events: none;

  z-index: 1;

  /*
   * As the screen becomes narrower, move more of the
   * banner off-screen instead of shrinking its height.
   *
   * This allows the title and banner to overlap.
   */
  @media (max-width: 1100px) {
    right: -6%;
  }

  @media (max-width: 900px) {
    right: -14%;
  }

  @media (max-width: 700px) {
    right: -25%;
  }

  @media (max-width: 520px) {
    right: -42%;
  }
`

const Logo = styled.img`
  width: clamp(88px, 9vw, 120px);
  height: auto;

  object-fit: contain;

  margin-bottom: clamp(22px, 3vw, 32px);
`

const MainHeading = styled.h1`
  position: relative;
  z-index: 4;

  margin: 0;

  color: ${DARK_GREEN};

  font-size: clamp(36px, 4vw, 54px);
  font-weight: 750;
  line-height: 1.05;

  letter-spacing: -0.02em;
`

const HeroText = styled.p`
  position: relative;
  z-index: 4;

  max-width: 520px;

  margin: clamp(18px, 2vw, 24px) 0 0;

  color: #333c45;

  font-size: clamp(16px, 1.4vw, 19px);
  line-height: 1.65;
`

/* =========================================================
   SECTIONS
   ========================================================= */

const Section = styled.section`
  width: 100%;

  box-sizing: border-box;

  padding:
    clamp(48px, 6vw, 78px)
    0;

  background: #fff;
`

const SoftSection = styled(Section)`
  background:
    linear-gradient(
      180deg,
      #fbfdfc 0%,
      #ffffff 100%
    );

  border-top: 1px solid #edf0ee;
  border-bottom: 1px solid #edf0ee;
`

const SearchSection = styled(Section)`
  position: relative;

  overflow: hidden;

  background:
    linear-gradient(
      180deg,
      #fbfdfc 0%,
      #f8fbf9 100%
    );

  &::before,
  &::after {
    content: '';

    position: absolute;

    width: 360px;
    height: 360px;

    border-radius: 50%;
    border: 1px solid rgba(0, 85, 46, 0.06);

    pointer-events: none;
  }

  &::before {
    left: -170px;
    bottom: -170px;
  }

  &::after {
    right: -170px;
    top: -170px;
  }
`

const SectionHeader = styled.div`
  width: min(720px, 100%);

  margin: 0 auto clamp(30px, 4vw, 42px);

  text-align: center;
`

const Eyebrow = styled.div`
  margin-bottom: 10px;

  color: ${GREEN};

  font-size: 12px;
  font-weight: 800;

  text-transform: uppercase;
  letter-spacing: 0.14em;
`

const SectionTitle = styled.h2`
  margin: 0;

  color: ${TEXT};

  font-size: clamp(28px, 3vw, 40px);
  line-height: 1.15;
`

const SectionDescription = styled.p`
  max-width: 660px;

  margin: 13px auto 0;

  color: ${MUTED};

  font-size: clamp(14px, 1.15vw, 16px);
  line-height: 1.6;
`

const SectionButton = styled(Link)`
  display: inline-flex;

  align-items: center;
  justify-content: center;

  margin-top: 20px;

  min-height: 40px;

  padding: 0 18px;

  border-radius: 6px;

  background: ${GREEN};
  color: #fff;

  text-decoration: none;

  font-size: 13px;
  font-weight: 700;

  box-shadow: 0 4px 12px rgba(0, 85, 46, 0.15);

  transition:
    background 180ms ease,
    transform 180ms ease;

  &:hover {
    background: ${DARK_GREEN};
    transform: translateY(-2px);
  }
`

/* =========================================================
   COHORT FEATURE CARD
   ========================================================= */

const CohortCarousel = styled.div`
  width: min(860px, 100%);
  margin: 0 auto;
`

const CohortFeatureCard = styled(Link)`
  width: 100%;

  box-sizing: border-box;

  padding:
    clamp(24px, 3vw, 34px)
    clamp(22px, 4vw, 40px);

  border: 1px solid ${BORDER};
  border-radius: 14px;

  background: #fff;

  color: ${TEXT};
  text-decoration: none;

  display: grid;

  grid-template-columns:
    auto
    minmax(0, 1fr)
    auto;

  align-items: center;

  gap: clamp(22px, 3vw, 34px);

  box-shadow:
    0 8px 24px
    rgba(0, 0, 0, 0.06);

  transition:
    transform 180ms ease,
    box-shadow 180ms ease,
    border-color 180ms ease;

  &:hover {
    transform: translateY(-4px);

    border-color: #b9c9bf;

    box-shadow:
      0 12px 32px
      rgba(0, 60, 32, 0.11);
  }

  &:hover .arrow {
    transform: translateX(5px);
  }

  @media (max-width: 650px) {
    grid-template-columns: auto 1fr;

    > .arrow {
      display: none;
    }
  }
`

const CohortLogoCircle = styled.div`
  width: clamp(76px, 8vw, 96px);
  height: clamp(76px, 8vw, 96px);

  flex: 0 0 auto;

  border-radius: 50%;

  border: 1px solid #dbe6df;

  background: ${LIGHT_GREEN};

  display: flex;
  align-items: center;
  justify-content: center;

  overflow: hidden;

  img {
    width: 88%;
    height: 88%;

    object-fit: contain;

    display: block;
  }
`

const CohortCardBody = styled.div`
  min-width: 0;

  h3 {
    margin: 0 0 6px;

    color: ${TEXT};

    font-size: clamp(22px, 2vw, 28px);
  }

  strong {
    display: block;

    margin-bottom: 8px;

    color: ${TEXT};

    font-size: 15px;
  }

  p {
    max-width: 650px;

    margin: 0;

    color: ${MUTED};

    font-size: 14px;
    line-height: 1.6;
  }
`

const CohortExploreText = styled.div`
  margin-top: 18px;

  color: ${GREEN};

  font-size: 13px;
  font-weight: 750;
`

const LargeArrow = styled.span`
  display: inline-block;

  color: ${GREEN};

  font-size: 28px;

  transition: transform 180ms ease;
`

const CarouselDots = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;

  gap: 10px;

  margin-top: 20px;
`

const Dot = styled.div`
  width: ${({ $active }) => ($active ? '10px' : '9px')};
  height: ${({ $active }) => ($active ? '10px' : '9px')};

  border-radius: 50%;

  background:
    ${({ $active }) =>
    $active
      ? GREEN
      : '#d9dedb'};
`

/* =========================================================
   DISEASE CARDS
   ========================================================= */

const DiseaseGrid = styled.div`
  width: min(1040px, 100%);

  margin: 0 auto;

  display: grid;

  grid-template-columns:
    repeat(3, minmax(0, 1fr));

  gap: clamp(18px, 2vw, 26px);

  @media (max-width: 850px) {
    grid-template-columns: 1fr;
  }
`

const DiseaseCard = styled.div`
  min-height: 280px;

  box-sizing: border-box;

  padding:
    clamp(24px, 3vw, 30px);

  border: 1px solid ${BORDER};
  border-radius: 14px;

  background: #fff;

  display: flex;
  flex-direction: column;

  transition:
    transform 180ms ease,
    box-shadow 180ms ease;

  &:hover {
    transform: translateY(-4px);

    box-shadow:
      0 9px 24px
      rgba(0, 0, 0, 0.07);
  }
`

const DiseaseIcon = styled.div`
  width: 68px;
  height: 68px;

  margin-bottom: 20px;

  border-radius: 50%;

  background: ${LIGHT_GREEN};

  color: ${GREEN};

  display: flex;
  align-items: center;
  justify-content: center;

  font-size: 30px;
`

const DiseaseName = styled.h3`
  margin: 0 0 10px;

  color: ${TEXT};

  font-size: 20px;
`

const DiseaseText = styled.p`
  flex: 1;

  margin: 0;

  color: ${MUTED};

  font-size: 14px;
  line-height: 1.6;
`

const ComingSoon = styled.div`
  margin-top: 24px;

  color: ${GREEN};

  font-size: 12px;
  font-weight: 800;

  text-transform: uppercase;
  letter-spacing: 0.08em;
`

/* =========================================================
   QUICK EXPLORE
   ========================================================= */

const SearchPanel = styled.div`
  position: relative;
  z-index: 2;

  width: min(700px, 100%);

  margin: 0 auto;

  box-sizing: border-box;

  padding:
    clamp(28px, 4vw, 38px)
    clamp(24px, 4vw, 40px);

  border: 1px solid ${BORDER};
  border-radius: 14px;

  background: #fff;

  box-shadow:
    0 9px 28px
    rgba(0, 0, 0, 0.07);
`

const SearchHeading = styled.div`
  margin-bottom: 20px;

  text-align: center;

  h3 {
    margin: 0 0 7px;

    color: ${TEXT};

    font-size: clamp(22px, 2vw, 27px);
  }

  p {
    margin: 0;

    color: ${MUTED};

    font-size: 14px;
  }
`

const ExampleText = styled.p`
  margin: 14px 0 0;

  color: ${MUTED};

  font-size: 12px;
  line-height: 1.5;

  text-align: center;
`

/* =========================================================
   FOOTER
   ========================================================= */

const Footer = styled.footer`
  width: 100%;

  border-top: 1px solid #e5e9e7;

  background: #fff;
`

const FooterMain = styled(Content)`
  padding:
    clamp(34px, 4vw, 50px)
    0;

  display: grid;

  grid-template-columns:
    1.6fr
    0.8fr
    0.8fr
    1fr;

  gap: clamp(28px, 4vw, 60px);

  @media (max-width: 850px) {
    grid-template-columns: 1fr 1fr;
  }

  @media (max-width: 520px) {
    grid-template-columns: 1fr;
  }
`

const FooterBrand = styled.div`
  display: grid;

  grid-template-columns:
    auto
    minmax(0, 1fr);

  gap: 16px;

  align-items: flex-start;
`

const FooterLogo = styled.img`
  width: 64px;
  height: auto;
`

const FooterBrandText = styled.div`
  strong {
    display: block;

    margin-bottom: 8px;

    font-size: 18px;
  }

  p {
    max-width: 260px;

    margin: 0;

    color: ${MUTED};

    font-size: 13px;
    line-height: 1.6;
  }
`

const FooterColumn = styled.div`
  h4 {
    margin: 0 0 15px;

    color: ${TEXT};

    font-size: 12px;
    font-weight: 800;

    text-transform: uppercase;
    letter-spacing: 0.05em;
  }

  a,
  span {
    display: block;

    margin: 9px 0;

    color: #39434d;

    font-size: 13px;

    text-decoration: none;
  }

  a:hover {
    color: ${GREEN};
  }
`

const FooterBottom = styled.div`
  width: 100%;

  padding: 14px 20px;

  box-sizing: border-box;

  background: ${GREEN};

  color: rgba(255, 255, 255, 0.88);

  text-align: center;

  font-size: 11px;
`

/* =========================================================
   HOME
   ========================================================= */

export default () => (
  <HomePage>

    <DocumentTitle />

    {/* =====================================================
        HERO
        ===================================================== */}

    <HeroSection>

      <HeroContent>

        <HeroCopy>

          <Logo
            src="/disease_atlas_logo.png"
            alt="WashU Disease Atlas logo"
          />

          <MainHeading>
            WashU Disease Atlas
          </MainHeading>

          <HeroText>
            Explore genomic variation, disease associations, and
            study-specific datasets across research cohorts to
            advance understanding of human disease.
          </HeroText>

        </HeroCopy>

      </HeroContent>

      <BannerImage
        src={bannerImage}
        alt=""
      />

    </HeroSection>


    {/* =====================================================
        EXPLORE BY COHORT
        ===================================================== */}

    <SoftSection>

      <Content>

        <SectionHeader>

          <Eyebrow>
            Cohort Exploration
          </Eyebrow>

          <SectionTitle>
            Explore by Cohort
          </SectionTitle>

          <SectionDescription>
            Browse genomic and clinical data organized by study cohort.
            View available datasets, analyses, variants, and
            cohort-specific resources.
          </SectionDescription>

          <SectionButton
            preserveSelectedDataset={false}
            to="/cohorts"
          >
            View all Cohorts →
          </SectionButton>

        </SectionHeader>


        <CohortCarousel>

          <CohortFeatureCard
            preserveSelectedDataset={false}
            to="/cohorts"
          >

            <CohortLogoCircle>
              <img
                src={ipnLogo}
                alt="IPN logo"
              />
            </CohortLogoCircle>


            <CohortCardBody>

              <h3>
                IPN
              </h3>

              <strong>
                Idiopathic Peripheral Neuropathy
              </strong>

              <p>
                Investigate genomic and phenotype data, including
                population structure, variant impacts, SNVs,
                small variants, and structural variation.
              </p>

              <CohortExploreText>
                Explore Cohort →
              </CohortExploreText>

            </CohortCardBody>


            <LargeArrow className="arrow">
              ›
            </LargeArrow>

          </CohortFeatureCard>


          <CarouselDots>

            <Dot $active />

            {/*
            Future cohorts:

            <Dot />
            <Dot />
            <Dot />
            <Dot />
            */}

          </CarouselDots>

        </CohortCarousel>

      </Content>

    </SoftSection>


    {/* =====================================================
        EXPLORE BY DISEASE
        ===================================================== */}

    <Section>

      <Content>

        <SectionHeader>

          <Eyebrow>
            Disease Exploration
          </Eyebrow>

          <SectionTitle>
            Explore by Disease
          </SectionTitle>

          <SectionDescription>
            Browse atlas content by disease area and discover associated
            genes, variants, cohorts, and genomic findings.
          </SectionDescription>

        </SectionHeader>


        <DiseaseGrid>

          <DiseaseCard>

            <DiseaseIcon>
              ⌁
            </DiseaseIcon>

            <DiseaseName>
              Disease Group 1
            </DiseaseName>

            <DiseaseText>
              Discover focused genetic findings, associated variants,
              genes, and available study cohorts within this disease area.
            </DiseaseText>

            <ComingSoon>
              Coming Soon
            </ComingSoon>

          </DiseaseCard>


          <DiseaseCard>

            <DiseaseIcon>
              ⌬
            </DiseaseIcon>

            <DiseaseName>
              Disease Group 2
            </DiseaseName>

            <DiseaseText>
              Explore disease-specific genomic associations and
              supporting datasets across participating studies.
            </DiseaseText>

            <ComingSoon>
              Coming Soon
            </ComingSoon>

          </DiseaseCard>


          <DiseaseCard>

            <DiseaseIcon>
              ◎
            </DiseaseIcon>

            <DiseaseName>
              Disease Group 3
            </DiseaseName>

            <DiseaseText>
              Browse genes, genomic regions, variants, and cohorts
              connected with this disease category.
            </DiseaseText>

            <ComingSoon>
              Coming Soon
            </ComingSoon>

          </DiseaseCard>

        </DiseaseGrid>

      </Content>

    </Section>


    {/* =====================================================
        QUICK EXPLORE
        ===================================================== */}

    <SearchSection>

      <Content>

        <SectionHeader>

          <Eyebrow>
            Direct Search
          </Eyebrow>

          <SectionTitle>
            Quick Explore
          </SectionTitle>

          <SectionDescription>
            Already know what you're looking for? Search directly by
            gene, genomic region, or variant across available datasets.
          </SectionDescription>

        </SectionHeader>


        <SearchPanel>

          <SearchHeading>

            <h3>
              Search the Disease Atlas
            </h3>

            <p>
              Select a cohort or search across all available datasets.
            </p>

          </SearchHeading>


          <Searchbox
            width="100%"
          />


          <ExampleText>

            Examples: &nbsp; Gene:{' '}

            <Link
              preserveSelectedDataset={false}
              to="/gene/PKD1"
            >
              PKD1
            </Link>

            &nbsp;&nbsp; Variant:{' '}

            <Link
              preserveSelectedDataset={false}
              to="/variant/16-2090943-G-A"
            >
              16-2090943-G-A
            </Link>

          </ExampleText>

        </SearchPanel>

      </Content>

    </SearchSection>


    {/* =====================================================
        FOOTER
        ===================================================== */}

    <Footer>

      <FooterMain>

        <FooterBrand>

          <FooterLogo
            src="/disease_atlas_logo.png"
            alt="WashU Disease Atlas"
          />

          <FooterBrandText>

            <strong>
              WashU Disease Atlas
            </strong>

            <p>
              A resource for exploring genomic variation and disease
              associations to advance human health research.
            </p>

          </FooterBrandText>

        </FooterBrand>


        <FooterColumn>

          <h4>
            Resources
          </h4>

          <Link
            preserveSelectedDataset={false}
            to="/cohorts"
          >
            Cohorts
          </Link>

          <span>
            Diseases
          </span>

          <span>
            Data Dictionary
          </span>

          <span>
            Documentation
          </span>

        </FooterColumn>


        <FooterColumn>

          <h4>
            Support
          </h4>

          <Link
            preserveSelectedDataset={false}
            to="/faq"
          >
            FAQ
          </Link>

          <Link
            preserveSelectedDataset={false}
            to="/contact"
          >
            Contact
          </Link>

          <Link
            preserveSelectedDataset={false}
            to="/terms"
          >
            Terms of Use
          </Link>

          <span>
            Privacy Policy
          </span>

        </FooterColumn>


        <FooterColumn>

          <h4>
            Connect
          </h4>

          <span>
            Washington University in St. Louis
          </span>

          <span>
            Department of Genetics
          </span>

        </FooterColumn>

      </FooterMain>


      <FooterBottom>
        © 2026 Washington University in St. Louis. All rights reserved.
      </FooterBottom>

    </Footer>

  </HomePage>
)